import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { RequestResults } from "../../common/RequestStatus";
import { fetchLanguageConfiguration, updateLanguageConfiguration } from "./languageConfigurationAsyncThunk";

export interface LanguageConfigurationData {
  id?: string;
  mainPageLanguages: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface LanguageConfigurationState {
  data: LanguageConfigurationData;
  status: RequestResults;
  loading: RequestResults;
  error: string | null;
}

const initialState: LanguageConfigurationState = {
  data: {
    mainPageLanguages: ['en', 'es', 'fr', 'de', 'pt', 'ar'],
  },
  status: RequestResults.idle,
  loading: RequestResults.idle,
  error: null,
};

const languageConfigurationSlice = createSlice({
  name: "languageConfiguration",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLanguageConfiguration.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchLanguageConfiguration.fulfilled, (state, action: PayloadAction<LanguageConfigurationData>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchLanguageConfiguration.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error((action.payload as string) || "Failed to fetch language configuration");
        state.error = (action.payload as string) || "Failed to fetch language configuration";
      })
      .addCase(updateLanguageConfiguration.pending, (state) => {
        state.loading = RequestResults.pending;
      })
      .addCase(updateLanguageConfiguration.fulfilled, (state, action) => {
        state.data = action.payload;
        toast.success("Language configuration saved successfully");
        state.loading = RequestResults.success;
      })
      .addCase(updateLanguageConfiguration.rejected, (state, action) => {
        const errMsg = (action.payload as string) || "Failed to update language configuration";
        state.error = errMsg;
        toast.error(errMsg);
        state.loading = RequestResults.failed;
      });
  },
});

export const selectLanguageConfiguration = (state: RootState) => state.languageConfiguration;

export default languageConfigurationSlice.reducer;
